<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { QUEUE_MODES, FILTER_OPERATORS } from './filters'
import { useDataSourcesStore } from '@/stores/dataSources'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  queue: {
    type: Array,
    default: () => []
  },
  anchorEl: {
    type: Object,
    default: null
  },
  initialSearchMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['apply', 'close', 'clear'])

const router = useRouter()
const dataSourcesStore = useDataSourcesStore()
const popupRef = ref(null)
const popupStyle = ref({})
const searchMode = ref(false)
const copied = ref(false)
let copiedTimeout = null

// Group queue items by mode for display
const fieldValueItems = computed(() =>
  props.queue.filter(q => q.mode === QUEUE_MODES.FIELD_VALUE)
)

const valueOnlyItems = computed(() =>
  props.queue.filter(q => q.mode === QUEUE_MODES.VALUE_ONLY)
)

const needsOperatorChoice = computed(() => props.queue.length > 1)

// Extract IP addresses from queue selections
const ipv4Re = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/
const ipv6Re = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/

const selectedIps = computed(() => {
  const ips = []
  for (const item of props.queue) {
    const val = String(item.rawValue)
    if (ipv4Re.test(val) || ipv6Re.test(val)) {
      ips.push(val)
    }
  }
  return [...new Set(ips)]
})

// Position popup above anchor element
function updatePosition() {
  if (!props.anchorEl || !popupRef.value) return

  const anchorRect = props.anchorEl.getBoundingClientRect()
  const popupRect = popupRef.value.getBoundingClientRect()
  const viewportWidth = window.innerWidth

  // Calculate position (prefer above anchor)
  let top = anchorRect.top - popupRect.height - 12
  let left = anchorRect.left + (anchorRect.width / 2) - (popupRect.width / 2)

  // If not enough space above, position below
  if (top < 10) {
    top = anchorRect.bottom + 12
  }

  // Keep within viewport horizontally
  if (left < 10) {
    left = 10
  } else if (left + popupRect.width > viewportWidth - 10) {
    left = viewportWidth - popupRect.width - 10
  }

  popupStyle.value = {
    top: `${top}px`,
    left: `${left}px`
  }
}

// Update position when shown
watch(() => props.show, async (isShown) => {
  if (isShown) {
    searchMode.value = props.initialSearchMode
    await nextTick()
    updatePosition()
  }
})

// Handle escape key
function handleKeydown(event) {
  if (event.key === 'Escape' && props.show) {
    if (searchMode.value) {
      searchMode.value = false
    } else {
      emit('close')
    }
  }
}

// Handle click outside
function handleClickOutside(event) {
  if (popupRef.value && !popupRef.value.contains(event.target)) {
    // Check if click was on anchor element
    if (props.anchorEl && props.anchorEl.contains(event.target)) {
      return // Let anchor handle its own click
    }
    searchMode.value = false
    emit('close')
  }
}

// Filter apply — dual behavior based on search mode
function handleApply(operator) {
  if (searchMode.value) {
    openAsNewSearch(operator)
  } else {
    emit('apply', operator)
  }
}

// Copy values to clipboard
async function handleCopy() {
  const values = props.queue.map(item => String(item.rawValue))
  const text = values.join(', ')
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
    if (copiedTimeout) clearTimeout(copiedTimeout)
    copiedTimeout = setTimeout(() => { copied.value = false }, 1500)
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    copied.value = true
    if (copiedTimeout) clearTimeout(copiedTimeout)
    copiedTimeout = setTimeout(() => { copied.value = false }, 1500)
  }
}

// Toggle search mode
function toggleSearchMode() {
  if (!needsOperatorChoice.value) {
    // Single selection — open immediately
    openAsNewSearch(FILTER_OPERATORS.ANYOF)
  } else {
    searchMode.value = !searchMode.value
  }
}

// Open selection as a new search in a new tab
function openAsNewSearch(operator) {
  const parts = props.queue.map(item => {
    const escaped = String(item.rawValue).replace(/"/g, '\\"')
    return item.mode === QUEUE_MODES.FIELD_VALUE
      ? `${item.field}:"${escaped}"`
      : `"${escaped}"`
  })
  const joiner = operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
  const query = parts.join(joiner)

  const routeQuery = { query }
  const sources = dataSourcesStore.serializedSources
  if (sources) routeQuery.sources = sources
  const route = router.resolve({ name: 'EventSearch', query: routeQuery })
  window.open(route.href, '_blank')

  searchMode.value = false
  emit('clear')
}

// Open selected IPs in IP search
function openIpSearch() {
  if (selectedIps.value.length === 0) return
  const type = selectedIps.value.length === 1 ? 'ip' : 'ip_table'
  const input = selectedIps.value.join(',')
  const route = router.resolve({ name: 'SearchResults', query: { input, type } })
  window.open(route.href, '_blank')
  searchMode.value = false
  emit('clear')
}

function handleClear() {
  searchMode.value = false
  emit('clear')
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('click', handleClickOutside, true)
  window.addEventListener('resize', updatePosition)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handleClickOutside, true)
  window.removeEventListener('resize', updatePosition)
  if (copiedTimeout) clearTimeout(copiedTimeout)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="popup">
      <div v-if="show" ref="popupRef" class="filter-popup" :class="{ 'search-mode': searchMode }" :style="popupStyle">
        <!-- Header -->
        <div class="popup-header" :class="{ faded: searchMode }">
          <span class="header-title">Apply Filter</span>
          <button class="close-btn" @click="$emit('close')" tabindex="-1">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Preview -->
        <div class="popup-preview" :class="{ faded: searchMode }">
          <!-- Field:Value items grouped by field -->
          <template v-if="fieldValueItems.length > 0">
            <div class="preview-group">
              <div class="preview-label">
                <font-awesome-icon :icon="['fas', 'equals']" />
                <span>Field match</span>
              </div>
              <div class="preview-items">
                <div v-for="item in fieldValueItems" :key="item.id" class="preview-item field-value">
                  <span class="item-field">{{ item.field }}:</span>
                  <span class="item-value">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- Value-only items -->
          <template v-if="valueOnlyItems.length > 0">
            <div class="preview-group">
              <div class="preview-label">
                <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                <span>Any field</span>
              </div>
              <div class="preview-items">
                <div v-for="item in valueOnlyItems" :key="item.id" class="preview-item value-only">
                  <span class="item-value">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Filter Actions -->
        <div class="popup-actions">
          <button
            @click="handleApply(FILTER_OPERATORS.ALL)"
            class="action-btn all"
            :class="{ 'search-highlight': searchMode }"
          >
            <font-awesome-icon :icon="['fas', 'layer-group']" />
            <span>AND</span>
          </button>
          <button
            @click="handleApply(FILTER_OPERATORS.ANYOF)"
            class="action-btn anyof"
            :class="{ 'search-highlight': searchMode }"
          >
            <font-awesome-icon :icon="['fas', 'circle-nodes']" />
            <span>OR</span>
          </button>
          <button
            @click="handleApply(FILTER_OPERATORS.NOT)"
            class="action-btn not"
            :class="{ faded: searchMode }"
          >
            <font-awesome-icon :icon="['fas', 'ban']" />
            <span>NOT</span>
          </button>
        </div>

        <!-- Utility Actions -->
        <div class="popup-utils" :class="{ faded: searchMode }">
          <button class="util-btn" @click="handleCopy" :class="{ 'copied': copied }">
            <font-awesome-icon :icon="['fas', copied ? 'check' : 'copy']" />
            <span>{{ copied ? 'Copied' : 'Copy' }}</span>
          </button>
          <button class="util-btn ip-btn" :disabled="selectedIps.length === 0" @click="openIpSearch">
            <font-awesome-icon :icon="['fas', 'globe']" />
            <span>IP{{ selectedIps.length > 1 ? 's' : '' }}</span>
          </button>
          <button class="util-btn search-btn" :class="{ active: searchMode }" @click="toggleSearchMode">
            <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
            <span>Search</span>
          </button>
        </div>

        <!-- Footer -->
        <div class="popup-footer" :class="{ faded: searchMode }">
          <button class="clear-btn" @click="handleClear">
            <font-awesome-icon :icon="['fas', 'trash']" />
            <span>Clear selection</span>
          </button>
        </div>

        <!-- Arrow -->
        <div class="popup-arrow"></div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.filter-popup {
  position: fixed;
  z-index: 1000;
  background: #1a1f28;
  border: 1px solid #2a3441;
  border-radius: 0.75rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  min-width: 280px;
  max-width: 360px;
  overflow: hidden;
}

/* Arrow */
.popup-arrow {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  background: #1a1f28;
  border-right: 1px solid #2a3441;
  border-bottom: 1px solid #2a3441;
  transform: translateX(-50%) rotate(45deg);
}

/* Header */
.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: opacity 0.2s ease;
}

.header-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #E5E7EB;
}

.close-btn {
  background: none;
  border: none;
  color: #6B7280;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

/* Preview */
.popup-preview {
  padding: 0.75rem 1rem;
  max-height: 200px;
  overflow-y: auto;
  transition: opacity 0.2s ease;
}

.popup-preview::-webkit-scrollbar {
  width: 4px;
}

.popup-preview::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 2px;
}

.preview-group {
  margin-bottom: 0.75rem;
}

.preview-group:last-child {
  margin-bottom: 0;
}

.preview-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6B7280;
  margin-bottom: 0.5rem;
}

.preview-label svg {
  font-size: 0.6rem;
}

.preview-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.preview-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
}

.preview-item.field-value {
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
}

.preview-item.value-only {
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.item-field {
  color: #9CA3AF;
}

.item-value {
  color: #E5E7EB;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Filter Actions */
.popup-actions {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.action-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.7rem;
  font-weight: 500;
}

.action-btn svg {
  font-size: 1rem;
}

.action-btn.anyof {
  background: rgba(115, 87, 210, 0.1);
  border-color: rgba(115, 87, 210, 0.3);
  color: #A78BFA;
}

.action-btn.anyof:hover {
  background: rgba(115, 87, 210, 0.2);
  border-color: #7357D2;
}

.action-btn.all {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3B82F6;
}

.action-btn.all:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: #3B82F6;
}

.action-btn.not {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #EF4444;
}

.action-btn.not:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: #EF4444;
}

/* Search mode: highlight AND/OR buttons */
.action-btn.search-highlight {
  animation: pulse-glow 1.5s ease-in-out infinite;
}

.action-btn.all.search-highlight {
  background: rgba(59, 130, 246, 0.2);
  border-color: #3B82F6;
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.3);
}

.action-btn.anyof.search-highlight {
  background: rgba(115, 87, 210, 0.2);
  border-color: #7357D2;
  box-shadow: 0 0 12px rgba(115, 87, 210, 0.3);
}

@keyframes pulse-glow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

/* Utility Actions */
.popup-utils {
  display: flex;
  gap: 0.5rem;
  padding: 0 1rem 0.75rem;
  transition: opacity 0.2s ease;
}

.util-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.45rem 0.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.375rem;
  color: #6B7280;
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.util-btn:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.15);
  color: #9CA3AF;
}

.util-btn svg {
  font-size: 0.7rem;
}

.util-btn.copied {
  color: #10B981;
  border-color: rgba(16, 185, 129, 0.3);
}

.util-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
  pointer-events: none;
}

/* Search button — not faded in search mode */
.util-btn.search-btn {
  transition: all 0.2s ease;
}

.search-mode .util-btn.search-btn {
  opacity: 1 !important;
  pointer-events: auto !important;
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #10B981;
}

.util-btn.search-btn.active {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #10B981;
}

/* Faded elements in search mode */
.faded {
  opacity: 0.2;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* Footer */
.popup-footer {
  padding: 0.5rem 1rem 0.75rem;
  transition: opacity 0.2s ease;
}

.clear-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #6B7280;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.clear-btn:hover {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.15);
  color: #9CA3AF;
}

/* Animation */
.popup-enter-active,
.popup-leave-active {
  transition: all 0.2s ease;
}

.popup-enter-from,
.popup-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
